/**
 * GPS Processing Pipeline for Y GO Tracking — v7 Maximum Continuity
 *
 * Pipeline:
 * 0. removeOutliers()       — Only remove truly impossible points (teleportation)
 * 1. interpolateGaps()      — Fill GPS gaps with intermediate points (aggressive)
 * 2. kalmanSmooth()         — Kalman filter (adaptive noise, gap-aware)
 * 3. simplifyPoints()       — Douglas-Peucker (conservative)
 * 4. buildSegments()        — Gap detection (only real long pauses)
 * 5. mergeCloseSegments()   — Rejoin close segments for continuity
 *
 * v7 changes: Maximum fluidity — aggressive interpolation, less splitting,
 * automatic segment merging. Goal: one continuous polyline like Webfleet telematics.
 */

// ============================================================
// CONSTANTS — v7: Maximum continuity for shuttle GPS
// ============================================================

const DEFAULT_GAP_TIME_MS = 600 * 1000;       // 10 min — only split on very long pauses
const DEFAULT_GAP_DISTANCE_M = 10000;         // 10 km — only split on impossible jumps
const DEFAULT_SMOOTH_WINDOW = 3;
const DEFAULT_DP_TOLERANCE = 0.000005;        // more detail preserved (was 0.000008)
const DEFAULT_MAX_POINTS = 8000;              // more points for smoother rendering (was 5000)
const DEFAULT_MAX_JUMP_M = 1000;              // 1km (was 800m) — very tolerant
const DEFAULT_MAX_SPEED_KMH = 250;
const DEFAULT_MIN_SPEED_TIME_S = 2;
const DEFAULT_MAX_CONSECUTIVE_SKIP = 4;        // Accept after 4 skips (was 5)
const DEFAULT_MAX_ACCURACY_M = 1500;           // 1.5 km (was 1km) — accept very weak GPS
const DEFAULT_MIN_SEGMENT_DISTANCE_M = 5;      // 5m (was 10m) — keep tiny segments too
const DEFAULT_INTERP_MAX_GAP_MS = 90000;       // 90s — au-delà on affiche un pointillé honnête
const DEFAULT_INTERP_STEP_MS = 2000;           // every 2s (was 3s) — denser interpolation
const DEFAULT_INTERP_MAX_DISTANCE_M = 1500;    // 1.5 km — gaps > 1.5km ne sont pas interpolés
const DEFAULT_MERGE_GAP_MS = 90000;            // 90s — merge uniquement les petits gaps (était 5 min)
const DEFAULT_MERGE_DISTANCE_M = 2000;         // 2 km — seuil distance aligné avec les 90s

// ============================================================
// HELPERS
// ============================================================

function haversineM(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function toMs(ts) {
  if (!ts) return 0;
  try { return new Date(ts).getTime(); } catch { return 0; }
}

function estimateSpeedKmh(recentPoints) {
  if (recentPoints.length < 2) return 0;
  const a = recentPoints[recentPoints.length - 2];
  const b = recentPoints[recentPoints.length - 1];
  const dist = haversineM(a, b);
  const dtS = (toMs(b.timestamp) - toMs(a.timestamp)) / 1000;
  if (dtS <= 0) return 0;
  return (dist / dtS) * 3.6;
}

function segmentTotalDistance(seg) {
  let total = 0;
  for (let i = 1; i < seg.length; i++) total += haversineM(seg[i - 1], seg[i]);
  return total;
}

/**
 * Compute bearing between two GPS points in degrees [0, 360).
 */
export function computeBearing(from, to) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(toRad(to.lat));
  const x = Math.cos(toRad(from.lat)) * Math.sin(toRad(to.lat)) -
            Math.sin(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/**
 * Assign forward-looking headings to each point within its segment.
 *
 * For each point we look AHEAD to the next point that is >= MIN_DIST_M away
 * within the same segment. Using a forward-looking bearing avoids the
 * classic replay bug where the arrow points backwards at segment starts
 * because it uses the bearing FROM the last position (which crossed a gap).
 *
 * The last point of each segment reuses the arrival bearing (prev → last).
 */
const HEADING_MIN_DIST_M = 5; // minimum distance to compute a meaningful bearing

export function assignHeadings(segments) {
  return segments.map((seg) => {
    if (!seg || seg.length < 2) return seg;
    return seg.map((pt, i) => {
      // Already has a reliable device-reported heading — keep it.
      // NOTE: heading=0 (due north) IS valid, only null/undefined means "not provided".
      if (pt.heading != null) return pt;

      let h = null;

      // Look AHEAD: find the next point >= HEADING_MIN_DIST_M away
      for (let j = i + 1; j < seg.length; j++) {
        const dist = haversineM(pt, seg[j]);
        if (dist >= HEADING_MIN_DIST_M) {
          h = computeBearing(pt, seg[j]);
          break;
        }
      }

      // Last point (or no forward point found): use arrival bearing
      if (h == null && i > 0) {
        for (let j = i - 1; j >= 0; j--) {
          const dist = haversineM(seg[j], pt);
          if (dist >= HEADING_MIN_DIST_M) {
            h = computeBearing(seg[j], pt);
            break;
          }
        }
      }

      return h != null ? { ...pt, heading: h } : pt;
    });
  });
}

// ============================================================
// STEP 0: REMOVE OUTLIERS — v7: very lenient
// ============================================================

export function removeOutliers(points, opts = {}) {
  if (!points || points.length <= 2) return points;

  const {
    maxJumpM = DEFAULT_MAX_JUMP_M,
    maxSpeedKmh = DEFAULT_MAX_SPEED_KMH,
    minSpeedTimeS = DEFAULT_MIN_SPEED_TIME_S,
    maxConsecutiveSkip = DEFAULT_MAX_CONSECUTIVE_SKIP,
    maxAccuracyM = DEFAULT_MAX_ACCURACY_M,
  } = opts;

  const maxSpeedMs = maxSpeedKmh / 3.6;
  const result = [points[0]];
  let lastGood = points[0];
  let skipCount = 0;
  const lastIndex = points.length - 1;

  for (let i = 1; i < points.length; i++) {
    const curr = points[i];
    const isLastPoint = i === lastIndex;

    // Accuracy filter — only reject extremely poor GPS (> 1km)
    if (curr.accuracy && curr.accuracy > maxAccuracyM) {
      // Always keep the last point (live position)
      if (isLastPoint) {
        result.push(curr); lastGood = curr; skipCount = 0;
        continue;
      }
      skipCount++;
      if (skipCount >= maxConsecutiveSkip) {
        result.push(curr); lastGood = curr; skipCount = 0;
      }
      continue;
    }

    const dist = haversineM(lastGood, curr);
    const tLast = toMs(lastGood.timestamp);
    const tCurr = toMs(curr.timestamp);
    const dtS = tLast > 0 && tCurr > 0 ? (tCurr - tLast) / 1000 : -1;

    let isOutlier = false;

    // v6: Only flag as outlier for truly impossible movements
    if (dtS > 0 && dtS < minSpeedTimeS) {
      // Very short interval: protect against timestamp jitter creating impossible speeds
      const impliedSpeedMs = dist / dtS;
      if (impliedSpeedMs > (maxSpeedMs * 1.6) && dist > 60) {
        isOutlier = true;
      }

      // Keep adaptive jump threshold as secondary guard
      const currentSpeed = estimateSpeedKmh(result);
      const adaptiveJump = currentSpeed < 10 ? 300 : currentSpeed < 50 ? maxJumpM : 1200;
      if (dist > adaptiveJump) isOutlier = true;

      // Low-quality fix spikes: poor accuracy + tiny dt + moderate jump
      if (!isOutlier && curr.accuracy && curr.accuracy > 120 && dist > 50) {
        isOutlier = true;
      }
    } else if (dtS >= minSpeedTimeS) {
      // Normal interval — check speed
      if ((dist / dtS) > maxSpeedMs) isOutlier = true;
    }

    if (isOutlier) {
      // Always keep the last point (live position)
      if (isLastPoint) {
        result.push(curr); lastGood = curr; skipCount = 0;
      } else {
        skipCount++;
        if (skipCount >= maxConsecutiveSkip) {
          result.push(curr); lastGood = curr; skipCount = 0;
        }
      }
    } else {
      result.push(curr); lastGood = curr; skipCount = 0;
    }
  }

  return result;
}

// ============================================================
// STEP 1: INTERPOLATE GAPS
// ============================================================

/**
 * Fill GPS gaps with interpolated points.
 * v7: Very aggressive interpolation — fills gaps up to 2 min and 3 km.
 */
export function interpolateGaps(points, opts = {}) {
  if (!points || points.length <= 1) return points;

  const {
    maxGapMs = DEFAULT_INTERP_MAX_GAP_MS,
    stepMs = DEFAULT_INTERP_STEP_MS,
    maxDistanceM = DEFAULT_INTERP_MAX_DISTANCE_M,
  } = opts;

  const result = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const p1 = points[i - 1];
    const p2 = points[i];

    const t1 = toMs(p1.timestamp);
    const t2 = toMs(p2.timestamp);
    const dt = t2 - t1;
    const dist = haversineM(p1, p2);

    // Interpolate short-to-medium gaps with reasonable distance
    if (dt > stepMs && dt <= maxGapMs && dist <= maxDistanceM && dist > 5) {
      const steps = Math.floor(dt / stepMs);
      for (let s = 1; s < steps; s++) {
        const ratio = s / steps;
        result.push({
          lat: p1.lat + (p2.lat - p1.lat) * ratio,
          lng: p1.lng + (p2.lng - p1.lng) * ratio,
          timestamp: new Date(t1 + ratio * dt).toISOString(),
          interpolated: true,
        });
      }
    }

    result.push(p2);
  }

  return result;
}

// ============================================================
// STEP 2: SMOOTH (Moving Average Filter) — kept for reference
// ============================================================

export function smoothPoints(points, windowSize = DEFAULT_SMOOTH_WINDOW) {
  if (!points || points.length <= windowSize) return points;

  const half = Math.floor(windowSize / 2);
  const result = [];

  for (let i = 0; i < points.length; i++) {
    if (i < half || i >= points.length - half) {
      result.push({ ...points[i] });
      continue;
    }

    let sumLat = 0, sumLng = 0, count = 0;
    for (let j = i - half; j <= i + half; j++) {
      const dt = Math.abs(toMs(points[j].timestamp) - toMs(points[i].timestamp));
      const dist = haversineM(points[j], points[i]);
      if (dt > DEFAULT_GAP_TIME_MS || dist > DEFAULT_GAP_DISTANCE_M) continue;
      sumLat += points[j].lat;
      sumLng += points[j].lng;
      count++;
    }

    if (count > 0) {
      result.push({ ...points[i], lat: sumLat / count, lng: sumLng / count });
    } else {
      result.push({ ...points[i] });
    }
  }

  return result;
}

// ============================================================
// STEP 2b: KALMAN FILTER (replaces Moving Average)
// ============================================================

/**
 * Kalman filter for GPS smoothing.
 * v7: Gap thresholds aligned with 10-min gap constant.
 */
export function kalmanSmooth(points, opts = {}) {
  if (!points || points.length <= 1) return points;

  const {
    processNoise = 0.00001,
    initialUncertainty = 1,
  } = opts;

  const result = [{ ...points[0] }];
  let estLat = points[0].lat;
  let estLng = points[0].lng;
  let pLat = initialUncertainty;
  let pLng = initialUncertainty;

  for (let i = 1; i < points.length; i++) {
    const curr = points[i];
    const prev = points[i - 1];

    const dt = Math.abs(toMs(curr.timestamp) - toMs(prev.timestamp));
    const dist = haversineM(prev, curr);

    // Reset filter on genuine gap (new segment — very long pause or huge distance)
    if (dt > DEFAULT_GAP_TIME_MS || dist > DEFAULT_GAP_DISTANCE_M) {
      estLat = curr.lat;
      estLng = curr.lng;
      pLat = initialUncertainty;
      pLng = initialUncertainty;
      result.push({ ...curr });
      continue;
    }

    // Predict: uncertainty grows proportionally to elapsed time
    const dtSec = Math.max(1, dt / 1000);
    const Q = processNoise * dtSec;
    pLat += Q;
    pLng += Q;

    // Measurement noise derived from GPS accuracy (meters → degrees²)
    const accDeg = curr.accuracy
      ? Math.max(0.000005, curr.accuracy / 111000)
      : 0.00005; // default ≈ 5.5m
    const R = accDeg * accDeg;

    // Kalman gain
    const kLat = pLat / (pLat + R);
    const kLng = pLng / (pLng + R);

    // Update estimate
    estLat += kLat * (curr.lat - estLat);
    estLng += kLng * (curr.lng - estLng);

    // Update covariance
    pLat *= (1 - kLat);
    pLng *= (1 - kLng);

    result.push({ ...curr, lat: estLat, lng: estLng });
  }

  return result;
}

// ============================================================
// STEP 3: SIMPLIFY (Douglas-Peucker)
// ============================================================

function perpendicularDistance(C, A, B) {
  const dx = B.lng - A.lng;
  const dy = B.lat - A.lat;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = C.lng - A.lng;
    const ey = C.lat - A.lat;
    return Math.sqrt(ex * ex + ey * ey);
  }
  const t = Math.max(0, Math.min(1, ((C.lng - A.lng) * dx + (C.lat - A.lat) * dy) / lenSq));
  return Math.sqrt((C.lng - (A.lng + t * dx)) ** 2 + (C.lat - (A.lat + t * dy)) ** 2);
}

function douglasPeucker(points, epsilon) {
  if (points.length <= 2) return points;
  let maxDist = 0, maxIdx = 0;
  const first = points[0], last = points[points.length - 1];
  for (let i = 1; i < points.length - 1; i++) {
    const dist = perpendicularDistance(points[i], first, last);
    if (dist > maxDist) { maxDist = dist; maxIdx = i; }
  }
  if (maxDist > epsilon) {
    const left = douglasPeucker(points.slice(0, maxIdx + 1), epsilon);
    const right = douglasPeucker(points.slice(maxIdx), epsilon);
    return left.slice(0, -1).concat(right);
  }
  return [first, last];
}

export function simplifyPoints(points, tolerance = DEFAULT_DP_TOLERANCE, maxPoints = DEFAULT_MAX_POINTS) {
  if (!points || points.length <= 10) return points;
  const simplified = douglasPeucker(points, tolerance);
  if (simplified.length <= maxPoints) return simplified;
  const step = (simplified.length - 1) / (maxPoints - 1);
  const result = [];
  for (let i = 0; i < maxPoints - 1; i++) result.push(simplified[Math.round(i * step)]);
  result.push(simplified[simplified.length - 1]);
  return result;
}

// ============================================================
// STEP 4: SEGMENT BUILDING — v7: only split on very long pauses
// ============================================================

export function buildSegments(points, gapTimeMs = DEFAULT_GAP_TIME_MS, gapDistanceM = DEFAULT_GAP_DISTANCE_M) {
  if (!points || points.length === 0) return [];

  const segments = [];
  let current = [{ lat: points[0].lat, lng: points[0].lng, timestamp: points[0].timestamp }];

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const tPrev = toMs(prev.timestamp);
    const tCurr = toMs(curr.timestamp);
    const dtMs = (tPrev > 0 && tCurr > 0) ? (tCurr - tPrev) : 0;
    const dist = haversineM(prev, curr);

    // segment_start is treated as a very soft hint (mobile app can over-emit this flag)
    let isGap = curr.segment_start === true && dtMs > (gapTimeMs / 2);

    if (!isGap) {
      // Split on long pauses only
      if (dtMs > gapTimeMs) isGap = true;
    }

    // Extreme short-window teleport guard only
    if (!isGap && dtMs > 0 && dtMs <= 15000 && dist > gapDistanceM) isGap = true;

    if (isGap && current.length > 0) { segments.push(current); current = []; }
    current.push({ lat: curr.lat, lng: curr.lng, timestamp: curr.timestamp });
  }

  if (current.length > 0) segments.push(current);
  return segments;
}

// ============================================================
// STEP 5: FILTER SHORT SEGMENTS — v7: keep nearly all segments
// ============================================================

export function filterSegments(segments, minDistanceM = DEFAULT_MIN_SEGMENT_DISTANCE_M) {
  return segments.filter(seg => seg.length >= 2 && segmentTotalDistance(seg) >= minDistanceM);
}

// ============================================================
// STEP 6: MERGE CLOSE SEGMENTS — v7: reconnect for continuity
// ============================================================

/**
 * Merge adjacent segments that are separated by a small gap.
 * Fills the gap with interpolated bridge points for a smooth visual transition.
 * Only truly large gaps (> mergeGapMs or > mergeDistM) remain as separate segments.
 */
export function mergeCloseSegments(segments, mergeGapMs = DEFAULT_MERGE_GAP_MS, mergeDistM = DEFAULT_MERGE_DISTANCE_M) {
  if (!segments || segments.length <= 1) return segments;

  const result = [segments[0].map(p => ({ ...p }))];

  for (let i = 1; i < segments.length; i++) {
    const prev = result[result.length - 1];
    const curr = segments[i];
    if (!prev.length || !curr.length) { result.push(curr.map(p => ({ ...p }))); continue; }

    const lastPt = prev[prev.length - 1];
    const firstPt = curr[0];
    const tLast = toMs(lastPt.timestamp);
    const tFirst = toMs(firstPt.timestamp);
    const dt = tFirst - tLast;
    const dist = haversineM(lastPt, firstPt);

    if (dt > 0 && dt <= mergeGapMs && dist <= mergeDistM) {
      // Create smooth bridge between segments with interpolated points
      const nSteps = Math.max(3, Math.min(20, Math.ceil(dt / 2000)));
      for (let s = 1; s < nSteps; s++) {
        const r = s / nSteps;
        // Use ease-in-out for smoother curve at connection points
        const ease = r < 0.5 ? 2 * r * r : 1 - Math.pow(-2 * r + 2, 2) / 2;
        prev.push({
          lat: lastPt.lat + (firstPt.lat - lastPt.lat) * ease,
          lng: lastPt.lng + (firstPt.lng - lastPt.lng) * ease,
          timestamp: new Date(tLast + r * dt).toISOString(),
          interpolated: true,
        });
      }
      // Append all points from current segment to previous
      for (const pt of curr) prev.push({ ...pt });
    } else {
      result.push(curr.map(p => ({ ...p })));
    }
  }

  return result;
}

// ============================================================
// FULL PIPELINE
// ============================================================

/**
 * Full GPS processing pipeline (v7 — Maximum Continuity):
 * raw → outliers → interpolate → kalman → simplify → segment → merge
 */
export function processGpsTrack(rawPoints, options = {}) {
  if (!rawPoints || rawPoints.length === 0) return [];
  if (rawPoints.length === 1) return [[{ lat: rawPoints[0].lat, lng: rawPoints[0].lng, timestamp: rawPoints[0].timestamp }]];

  // Sort by timestamp strictly before any processing
  const sorted = [...rawPoints].sort((a, b) => toMs(a.timestamp) - toMs(b.timestamp));

  const {
    maxJumpM = DEFAULT_MAX_JUMP_M,
    maxSpeedKmh = DEFAULT_MAX_SPEED_KMH,
    maxAccuracyM = DEFAULT_MAX_ACCURACY_M,
    interpMaxGapMs = DEFAULT_INTERP_MAX_GAP_MS,
    interpStepMs = DEFAULT_INTERP_STEP_MS,
    interpMaxDistanceM = DEFAULT_INTERP_MAX_DISTANCE_M,
    dpTolerance = DEFAULT_DP_TOLERANCE,
    maxPoints = DEFAULT_MAX_POINTS,
    gapTimeMs = DEFAULT_GAP_TIME_MS,
    gapDistanceM = DEFAULT_GAP_DISTANCE_M,
    minSegmentDistanceM = DEFAULT_MIN_SEGMENT_DISTANCE_M,
    mergeGapMs = DEFAULT_MERGE_GAP_MS,
    mergeDistM = DEFAULT_MERGE_DISTANCE_M,
  } = options;

  // Step 0: Remove outliers (lenient — only impossible points)
  const cleaned = removeOutliers(sorted, { maxJumpM, maxSpeedKmh, maxAccuracyM });

  // Step 1: Interpolate gaps (aggressive — fill holes up to 2 min)
  const interpolated = interpolateGaps(cleaned, {
    maxGapMs: interpMaxGapMs, stepMs: interpStepMs, maxDistanceM: interpMaxDistanceM,
  });

  // Step 2: Kalman filter for smooth trajectory
  const smoothed = kalmanSmooth(interpolated);

  // Step 3: Simplify (conservative — keep more detail)
  const simplified = simplifyPoints(smoothed, dpTolerance, maxPoints);

  // Step 4: Segment (only on genuine long pauses)
  const rawSegments = buildSegments(simplified, gapTimeMs, gapDistanceM);

  // Step 5: Filter — but always keep the last segment (contains live position)
  if (rawSegments.length === 0) return [];
  const lastSeg = rawSegments[rawSegments.length - 1];
  const filtered = filterSegments(rawSegments.slice(0, -1), minSegmentDistanceM);
  if (lastSeg.length >= 1) filtered.push(lastSeg);

  // Step 6: Merge close segments — rejoin for maximum continuity
  const merged = mergeCloseSegments(filtered, mergeGapMs, mergeDistM);

  // Step 7: Assign forward-looking headings so the replay arrow always points
  // in the correct direction of travel (bearing to next point in same segment).
  return assignHeadings(merged);
}

// ============================================================
// TRIM SEGMENTS TO TIMESTAMP (for replay)
// ============================================================

export function trimSegmentsToTimestamp(segments, cutoffTimestamp) {
  if (!segments || !segments.length) return segments;
  const cutoffMs = typeof cutoffTimestamp === 'number' ? cutoffTimestamp : toMs(cutoffTimestamp);
  if (!cutoffMs) return segments;

  const result = [];
  for (const seg of segments) {
    const trimmed = [];
    for (let i = 0; i < seg.length; i++) {
      const t = seg[i].timestamp ? toMs(seg[i].timestamp) : 0;
      if (t && t > cutoffMs) {
        if (trimmed.length > 0 && i > 0) {
          const prev = seg[i - 1];
          const tPrev = prev.timestamp ? toMs(prev.timestamp) : 0;
          if (tPrev && tPrev <= cutoffMs) {
            const ratio = (cutoffMs - tPrev) / (t - tPrev);
            trimmed.push({
              lat: prev.lat + (seg[i].lat - prev.lat) * ratio,
              lng: prev.lng + (seg[i].lng - prev.lng) * ratio,
              timestamp: new Date(cutoffMs).toISOString(),
            });
          }
        }
        break;
      }
      trimmed.push(seg[i]);
    }
    if (trimmed.length >= 1) result.push(trimmed);
  }
  return result;
}

// ============================================================
// ASSIGN TIMESTAMPS TO SNAPPED POINTS
// ============================================================

export function assignTimestampsToSnapped(snappedPts, originalSeg) {
  if (!snappedPts || !snappedPts.length) return snappedPts;
  if (!originalSeg || !originalSeg.length) return snappedPts;
  if (!originalSeg[0]?.timestamp || !originalSeg[originalSeg.length - 1]?.timestamp) return snappedPts;

  const startMs = toMs(originalSeg[0].timestamp);
  const endMs = toMs(originalSeg[originalSeg.length - 1].timestamp);
  if (endMs <= startMs) return snappedPts;

  const cumDist = [0];
  for (let j = 1; j < snappedPts.length; j++) {
    const dlat = snappedPts[j].lat - snappedPts[j - 1].lat;
    const dlng = snappedPts[j].lng - snappedPts[j - 1].lng;
    cumDist.push(cumDist[j - 1] + Math.sqrt(dlat * dlat + dlng * dlng));
  }
  const totalDist = cumDist[cumDist.length - 1] || 1;

  return snappedPts.map((p, j) => ({
    ...p,
    timestamp: new Date(startMs + (cumDist[j] / totalDist) * (endMs - startMs)).toISOString(),
  }));
}

// ============================================================
// SNAP-TO-ROAD INTEGRATION
// ============================================================

export function applySnappedRoad(snappedPoints, originalSegments) {
  if (!snappedPoints || snappedPoints.length === 0) return originalSegments;
  return [snappedPoints.map(p => ({ lat: p.lat, lng: p.lng }))];
}
